/**
 * Normalizes any supported messaging format (HTML, Slack mrkdwn, plain text)
 * to standard Markdown.
 *
 * Used at template publish time so the virtual notification node always
 * stores canonical Markdown regardless of what channel the original
 * workflow targeted. The runtime channel converter handles the other
 * direction (Markdown → channel-native).
 */
export function normalizeToMarkdown(text: string): string {
  if (!text) return "";

  let result = text;

  // Slack link syntax uses angle brackets — convert before stripping HTML so
  // these aren't swallowed by the tag-stripping regex below.
  result = result.replace(/<(https?:\/\/[^|>]+)\|([^>]+)>/g, "[$2]($1)");
  result = result.replace(/<(https?:\/\/[^>]+)>/g, "$1");

  result = result.replace(/(?<![~])~([^~\n]+)~(?![~])/g, "~~$1~~");

  result = result.replace(/<a\b[^>]*?\shref=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, "[$2]($1)");

  result = result.replace(/<pre>([\s\S]*?)<\/pre>/gi, "```\n$1\n```");
  result = result.replace(/<code>([\s\S]*?)<\/code>/gi, "`$1`");

  result = result.replace(/<(?:b|strong)>([\s\S]*?)<\/(?:b|strong)>/gi, "**$1**");
  result = result.replace(/<(?:i|em)>([\s\S]*?)<\/(?:i|em)>/gi, "*$1*");
  result = result.replace(/<u>([\s\S]*?)<\/u>/gi, "**$1**");
  result = result.replace(/<(?:s|del)>([\s\S]*?)<\/(?:s|del)>/gi, "~~$1~~");

  result = result.replace(/<br\s*\/?>/gi, "\n");
  result = result.replace(/<\/p>\s*<p[^>]*>/gi, "\n\n");
  result = result.replace(/<\/?p[^>]*>/gi, "");

  result = result.replace(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/gi, (_, inner: string) =>
    inner
      .split("\n")
      .map(line => `> ${line}`)
      .join("\n"),
  );

  result = result.replace(/<\/?[a-z][^>]*>/gi, "");

  // Decode HTML entities in a single left-to-right pass so escaped sequences
  // like `&amp;#60;` decode to the literal `&#60;` instead of being double-decoded to `<`.
  result = result.replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
    if (code[0] === "#") {
      const hex = code[1] === "x" || code[1] === "X";
      const n = parseInt(code.slice(hex ? 2 : 1), hex ? 16 : 10);
      return Number.isFinite(n) && n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : match;
    }
    return HTML_ENTITIES[code.toLowerCase()] ?? match;
  });

  return result;
}

const HTML_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

/**
 * Rewrites Slack single-asterisk bold (`*bold*`) to Markdown double-asterisk.
 * Only safe when the source is known to be Slack — otherwise it would mangle
 * genuine Markdown italic. Negative lookaround preserves existing `**...**`.
 */
export function slackBoldToMarkdown(text: string): string {
  if (!text) return "";
  return text.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, "**$1**");
}
